import { useCallback, useEffect } from 'react';
import {
  resetBundleRetrievePage,
  selectBundleRetrieveActivePageIndex,
  selectBundleRetrieveBasePageId,
  selectBundleRetrievePageIds,
  selectBundleRetrievePageSelectedCheckboxes,
  selectSharedDataActiveBundleType,
  selectSharedDataBundleId,
  setBundleRetrieveActivePageIndex,
  setBundleRetrievePageScaleZoomIn,
  setBundleRetrievePageScaleZoomOut,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { useLazyGetBundlePageFindMissingQuery } from '@/api/bundle/bundleFindMissing';
import { useLazyPostBundleRetrieveMissingQuery } from '@/api';

export const useRetrievePagesDialog = () => {
  const dispatch = useAppDispatch();

  const bundleId = useAppSelector(selectSharedDataBundleId);
  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const bundleRetrieveBasePageId = useAppSelector(selectBundleRetrieveBasePageId);
  const bundleRetrieveActivePageIndex = useAppSelector(selectBundleRetrieveActivePageIndex);
  const bundleRetrievePageIds = useAppSelector(selectBundleRetrievePageIds);
  const bundleRetrievePageSelectedCheckboxes = useAppSelector(selectBundleRetrievePageSelectedCheckboxes);

  const [triggerGetBundlePageFindMissing, { isLoading: isGetBundlePageFindMissingLoading }] =
    useLazyGetBundlePageFindMissingQuery();

  const [triggerPostBundleRetrieveMissing, { isFetching: isRetrieving }] = useLazyPostBundleRetrieveMissingQuery();

  useEffect(() => {
    return () => {
      if (activeBundleType) {
        dispatch(resetBundleRetrievePage({ key: activeBundleType }));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeBundleType && bundleRetrieveBasePageId) {
      triggerGetBundlePageFindMissing({
        pageId: bundleRetrieveBasePageId,
        windowSize: 10,
      })
        .unwrap()
        .then((data) => {
          const activePageIndex = data?.findIndex((pageId) => pageId === bundleRetrieveBasePageId);
          dispatch(
            setBundleRetrieveActivePageIndex({
              key: activeBundleType,
              data: activePageIndex === -1 ? 0 : activePageIndex,
            }),
          );
        });
    }
  }, [activeBundleType, bundleRetrieveBasePageId, dispatch, triggerGetBundlePageFindMissing]);

  const pageZoomIn = useCallback(() => {
    if (activeBundleType) {
      dispatch(setBundleRetrievePageScaleZoomIn({ key: activeBundleType }));
    }
  }, [activeBundleType, dispatch]);

  const pageZoomOut = useCallback(() => {
    if (activeBundleType) {
      dispatch(setBundleRetrievePageScaleZoomOut({ key: activeBundleType }));
    }
  }, [activeBundleType, dispatch]);

  const navigatePageUp = useCallback(() => {
    if (activeBundleType && bundleRetrieveActivePageIndex > 0) {
      dispatch(
        setBundleRetrieveActivePageIndex({
          key: activeBundleType,
          data: bundleRetrieveActivePageIndex - 1,
        }),
      );
    }
  }, [activeBundleType, bundleRetrieveActivePageIndex, dispatch]);

  const navigatePageDown = useCallback(() => {
    if (activeBundleType && bundleRetrieveActivePageIndex > 0) {
      dispatch(
        setBundleRetrieveActivePageIndex({
          key: activeBundleType,
          data: bundleRetrieveActivePageIndex + 1,
        }),
      );
    }
  }, [activeBundleType, bundleRetrieveActivePageIndex, dispatch]);

  const handleRetrieveButtonClick = useCallback(() => {
    if (bundleId && bundleRetrieveBasePageId) {
      const baseIndex = bundleRetrievePageIds.indexOf(bundleRetrieveBasePageId);

      if (baseIndex !== -1) {
        const pagesBefore = bundleRetrievePageIds
          .slice(0, baseIndex)
          .filter((pageId) => bundleRetrievePageSelectedCheckboxes.includes(pageId));
        const pagesAfter = bundleRetrievePageIds
          .slice(baseIndex + 1)
          .filter((pageId) => bundleRetrievePageSelectedCheckboxes.includes(pageId));

        return triggerPostBundleRetrieveMissing({
          bundleId: bundleId,
          pageId: bundleRetrieveBasePageId,
          pagesBefore: pagesBefore,
          pagesAfter: pagesAfter,
        }).unwrap();
      }
    }
  }, [
    bundleId,
    bundleRetrieveBasePageId,
    bundleRetrievePageIds,
    bundleRetrievePageSelectedCheckboxes,
    triggerPostBundleRetrieveMissing,
  ]);

  return {
    isLoading: isGetBundlePageFindMissingLoading,
    isRetrieving,
    bundleRetrievePageSelectedCheckboxes,
    pageZoomIn,
    pageZoomOut,
    navigatePageUp,
    navigatePageDown,
    handleRetrieveButtonClick,
  };
};
